import React, { useState, useEffect } from 'react';

const LEVEL_COLORS = {
  1: 'bg-gray-100 text-gray-700',
  2: 'bg-green-100 text-green-700',
  3: 'bg-blue-100 text-blue-700',
  4: 'bg-purple-100 text-purple-700',
  5: 'bg-amber-100 text-amber-700',
  6: 'bg-orange-100 text-orange-700',
  7: 'bg-rose-100 text-rose-700',
};

const LEVEL_THRESHOLDS = [0, 400, 1000, 2000, 3600, 5600, 8400];

const XPProgressBar = ({ totalXp, level }) => {
  const currentThreshold = LEVEL_THRESHOLDS[level - 1] ?? 0;
  const nextThreshold = LEVEL_THRESHOLDS[level] ?? null;

  if (nextThreshold === null) {
    return <p className="text-xs text-gray-500">Maximální level dosažen!</p>;
  }

  const progress = Math.min(
    ((totalXp - currentThreshold) / (nextThreshold - currentThreshold)) * 100,
    100
  );

  return (
    <div className="w-full max-w-xs">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>{totalXp} XP</span>
        <span>Level {level + 1}: {nextThreshold} XP</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-yellow-400 rounded-full transition-all duration-700"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

const LEVELS = [
  [0,    1, 'Začátečník'],
  [400,  2, 'Zvídavý student'],
  [1000, 3, 'Pilný žák'],
  [2000, 4, 'Pokročilý angličtinář'],
  [3600, 5, 'Zdatný mluvčí'],
  [5600, 6, 'Znalec angličtiny'],
  [8400, 7, 'Mistr angličtiny'],
];

function computeLevel(totalXp) {
  let result = [1, 'Začátečník'];
  for (const [threshold, num, name] of LEVELS) {
    if (totalXp >= threshold) result = [num, name];
    else break;
  }
  return result;
}

const XPBadge = ({ xpEarned, totalXp, level: levelProp, levelName: levelNameProp, levelUp }) => {
  const [showLevelUp, setShowLevelUp] = useState(levelUp);

  useEffect(() => {
    setShowLevelUp(levelUp);
    if (levelUp) {
      const t = setTimeout(() => setShowLevelUp(false), 4000);
      return () => clearTimeout(t);
    }
  }, [levelUp]);

  // Dashboard mode: only totalXp provided — compute level client-side
  if (xpEarned == null && totalXp == null) return null;

  const [level, levelName] = (levelProp != null && levelNameProp != null)
    ? [levelProp, levelNameProp]
    : computeLevel(totalXp ?? 0);

  const levelColor = LEVEL_COLORS[level] || LEVEL_COLORS[1];

  return (
    <div className="mt-4 flex flex-col items-center gap-2">
      {xpEarned != null && (
        <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 font-semibold text-sm">
          +{xpEarned} XP
        </div>
      )}

      <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${levelColor}`}>
        Úroveň {level}: {levelName}
      </div>

      <XPProgressBar totalXp={totalXp ?? 0} level={level} />

      {showLevelUp && (
        <div className="w-full mt-2 p-3 rounded-lg bg-amber-50 border border-amber-300 text-center">
          <p className="font-bold text-amber-800 text-sm">
            Postoupil/a jsi na úroveň {level}: {levelName}!
          </p>
        </div>
      )}
    </div>
  );
};

export { computeLevel, LEVEL_COLORS };
export default XPBadge;
